
import SaldoInvestimentoModal from './components/modals/SaldoInvestimentoModal';

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import PaginationBar from './components/PaginationBar';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import useReferenceData from './components/hooks/useReferenceData';
import usePagination10 from './components/hooks/usePagination10';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import { API_BASE_URL } from '../config/ApiConfig';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';

const emptyForm = {
  ano: '', idPrograma: '', idEscola: '', idConta: '', inicial: '', saldoFinal: ''
};

function keyOf(item) {
  return `${item.ano}_${item.idPrograma}_${item.idEscola}_${item.idConta}`;
}


function formatCurrency(value) {
  if (value == null) return '';
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function SaldosInvestimento() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const [items, setItems] = useState([]);
  const [sortField, setSortField] = useState('idConta');
  const [sortDir, setSortDir] = useState('asc'); // 'asc' or 'desc'
  const [form, setForm] = useState({ ...emptyForm, idEscola: cnpj || '', ano: ano || '' });
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(items.length);
  const { programas, contas } = useReferenceData(token, cnpj);
  function getContaLabel(item) {
    // prefer label from reference `contas` if available
    const c = (contas || []).find(ct => String(ct.idConta) === String(item.idConta) || String(ct.numConta) === String(item.idConta));
    if (c) return (c.nomeConta || c.numConta || c.idConta || '').toString();
    return (item._contaLabel || item.idConta || '').toString();
  }

  function getContaSortKey(item) {
    // Try to get the numeric account identifier (e.g. "5434-8" => 5434, "35337-X" => 35337)
    const c = (contas || []).find(ct => String(ct.idConta) === String(item.idConta) || String(ct.numConta) === String(item.idConta));
    const raw = c ? (c.numConta || c.idConta || '') : (item._contaLabel || item.idConta || '');
    const s = String(raw).trim();
    const m = s.match(/^(\d+)/);
    const num = m ? parseInt(m[1], 10) : NaN;
    // suffix after hyphen or non-numeric part
    const sufMatch = s.match(/-(.*)$/);
    const suffix = sufMatch ? sufMatch[1].toString().toUpperCase() : '';
    return { raw: s, num, suffix };
  }
  // Compute sorted items then apply pagination
  const sortedItems = React.useMemo(() => {
    if (!sortField) return items;
    const copy = [...items];
    copy.sort((a, b) => {
      if (sortField === 'idConta') {
        const ka = getContaSortKey(a);
        const kb = getContaSortKey(b);
        // if both have numeric part, compare numerically
        if (!isNaN(ka.num) && !isNaN(kb.num) && ka.num !== kb.num) {
          return (ka.num - kb.num) * (sortDir === 'asc' ? 1 : -1);
        }
        // if numeric parts equal or missing, compare suffix (e.g., '8' vs 'X')
        if (ka.num === kb.num) {
          const s = ka.suffix.localeCompare(kb.suffix, 'pt-BR', { sensitivity: 'base' });
          if (s !== 0) return s * (sortDir === 'asc' ? 1 : -1);
        }
        // fallback to full raw string compare
        return ka.raw.localeCompare(kb.raw, 'pt-BR', { sensitivity: 'base' }) * (sortDir === 'asc' ? 1 : -1);
      }
      let va = a[sortField] == null ? '' : String(a[sortField]);
      let vb = b[sortField] == null ? '' : String(b[sortField]);
      // alphabetical comparison (locale-aware)
      return va.localeCompare(vb, 'pt-BR', { sensitivity: 'base' }) * (sortDir === 'asc' ? 1 : -1);
    });
    return copy;
  }, [items, sortField, sortDir, contas]);

  const displayed = sortedItems.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  const resumoPorPrograma = React.useMemo(() => {
    const map = {};
    (Array.isArray(items) ? items : []).forEach(it => {
      const chave = String(it.idPrograma);
      if (!map[chave]) map[chave] = { idPrograma: it.idPrograma, inicial: 0, saldoFinal: 0, qtd: 0 };
      map[chave].inicial += parseFloat(it.inicial) || 0;
      map[chave].saldoFinal += parseFloat(it.saldoFinal) || 0;
      map[chave].qtd += 1;
    });
    return Object.values(map);
  }, [items]);

  useEffect(() => {
    if (!cnpj || !ano) { showDefaultModal('error', 'Escola ou ano não selecionado.'); setIsLoading(false); return; }
    setIsLoading(true);
    axios.get(`${API_BASE_URL.SALDO_INVESTIMENTO}/search`, {
      params: { idEscola: cnpj, ano }, headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => {
        setItems(r.data);
      })
      .catch(err => {
        if (err.response && err.response.status !== 200 && err.response.status !== 404) {
          showDefaultModal('error', 'Erro ao carregar saldos.');
        }
      })
      .finally(() => setIsLoading(false));
  }, [token, cnpj, ano, atualizar]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }
  function handleNew() {
    setForm({ ...emptyForm, idEscola: cnpj || '', ano: ano || '', _isEditing: false });
    setShowModal(true);
  }
  function handleEdit() {
    if (selected.length !== 1) return;
    const s = selected[0];
    setForm({ ...s, _isEditing: true });
    setShowModal(true);
  }
  function handleSave(e) {
    e.preventDefault();
    // Salvar saldo usando padrão axios.get com params
    axios.post(`${API_BASE_URL.SALDO_INVESTIMENTO}/save`, form, {
      headers: { Authorization: token ? `Bearer ${token}` : undefined }
    })
      .then(() => {
        setShowModal(false);
        setAtualizar(a => !a);
        setSelected([]);
      })
      .catch(() => showDefaultModal('error', 'Erro ao salvar saldo.'));
  }
  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () => Promise.all(selected.map(s =>
      axios.delete(`${API_BASE_URL.SALDO_INVESTIMENTO}/delete/${s.ano}/${s.idPrograma}/${s.idEscola}/${s.idConta}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined }
      })
    ))
      .then(() => { setSelected([]); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao excluir saldo.'))
    );
  }
  function toggleSelect(item) {
    setSelected(prev => prev.find(s => keyOf(s) === keyOf(item)) ? [] : [item]);
  }

  function handleSort(field) {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    // reset to first page when sorting
    setPagination({ startIndex: 0, rowsPerPage: setPagination.rowsPerPage });
  }

  // Função para importar JSON com relatório detalhado
  const [importReport, setImportReport] = useState(null);
  function handleImportJson(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async evt => {
      try {
        const data = JSON.parse(evt.target.result);
        if (!Array.isArray(data.contas)) throw new Error('JSON inválido: contas não é array');
        const novosSaldos = data.contas.map(c => {
          const contaObj = (contas || []).find(ct => ct.numConta === c.conta || ct.idConta === c.conta);
          return {
            ano: ano || '',
            idPrograma: contaObj ? contaObj.idPrograma : '',
            idEscola: cnpj || '',
            idConta: c.conta,
            inicial: c.saldoInicial,
            saldoFinal: c.saldoFinal,
            _contaLabel: contaObj ? (contaObj.nomeConta || contaObj.numConta || contaObj.idConta) : c.conta
          };
        });
        // Executa cada requisição individualmente para capturar o resultado
        const results = await Promise.all(novosSaldos.map(async saldo => {
          try {
            await axios.post(`${API_BASE_URL.SALDO_INVESTIMENTO}/save`, saldo, {
              headers: { Authorization: token ? `Bearer ${token}` : undefined }
            });
            return { conta: saldo._contaLabel, status: 'sucesso', message: null };
          } catch (err) {
            let msg = 'Erro desconhecido';
            if (err.response && err.response.data && err.response.data.message) {
              msg = err.response.data.message;
            } else if (err.response && err.response.status === 403) {
              msg = 'Acesso negado (403)';
            }
            return { conta: saldo._contaLabel, status: 'erro', message: msg };
          }
        }));
        setAtualizar(a => !a);
        setImportReport(results);
      } catch {
        showDefaultModal('error', 'JSON inválido.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  // Ref para input file
  const fileInputRef = React.useRef();

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="SALDOS INVESTIMENTO" icon="bi-bank2" />
        <PageContent>
          <Toolbar onNew={handleNew} onEdit={handleEdit} disableEdit={selected.length !== 1} onDelete={handleDelete} disableDelete={selected.length === 0}>
            <button className="btn btn-info btn-sm botao-igual" onClick={() => fileInputRef.current && fileInputRef.current.click()}><i className="bi bi-upload me-1"></i>Importar Json</button>
            <input type="file" accept="application/json" ref={fileInputRef} style={{ display: 'none' }} onChange={handleImportJson} />
          </Toolbar>
          {/* Relatório de importação detalhado */}
          {importReport && (
            <div className="alert alert-info" style={{ fontSize: '0.95em' }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>Resultado da importação:</strong>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setImportReport(null)}>Fechar</button>
              </div>
              <table className="table table-bordered table-sm mb-0">
                <thead>
                  <tr>
                    <th>Conta</th>
                    <th>Status</th>
                    <th>Mensagem</th>
                  </tr>
                </thead>
                <tbody>
                  {importReport.map((r, idx) => (
                    <tr key={idx} className={r.status === 'sucesso' ? 'table-success' : 'table-danger'}>
                      <td>{r.conta}</td>
                      <td>{r.status}</td>
                      <td>{r.message || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {resumoPorPrograma.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-2" style={{ maxHeight: '30vh', overflowY: 'auto', overflowX: 'hidden', flexShrink: 0 }}>
              {resumoPorPrograma.map(prog => {
                const programa = (programas || []).find(p => String(p.id) === String(prog.idPrograma));
                return (
                  <div className="card border-0 shadow-sm" key={prog.idPrograma} style={{ borderRadius: 10, flex: '1 1 0', minWidth: 200, maxWidth: '25%' }}>
                    <div className="card-body pt-2 pb-1 px-3">
                      <div className="d-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: '#198754', color: '#fff', margin: '-0.5rem -1rem 0.5rem', padding: '0.15rem 1rem', borderRadius: '10px 10px 0 0', minHeight: '22px' }}>
                        <span className="fw-bold text-truncate" style={{ fontSize: '.62rem' }} title={prog.idPrograma}>{programa ? programa.descricao : prog.idPrograma}</span>
                        <span className="badge text-bg-light text-success flex-shrink-0" style={{ fontSize: '.62rem' }}>{prog.qtd}</span>
                      </div>
                      <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                        <span className="text-muted">Saldo Inicial</span><span>{formatCurrency(prog.inicial)}</span>
                      </div>
                      <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                        <span className="text-muted">Saldo Final</span><span>{formatCurrency(prog.saldoFinal)}</span>
                      </div>
                      <div className="d-flex justify-content-between pt-1 mt-1 border-top fw-bold" style={{ fontSize: '.78rem', whiteSpace: 'nowrap' }}>
                        <span>Total</span><span className="text-success">{formatCurrency(prog.saldoFinal)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <TableContainer isLoading={false}>
              <DataTable head={
                  <tr>
                    <th>#</th>
                    <th>Ano</th>
                    <th>Programa</th>
                    <th>Conta</th>
                    <th className='text-end'>Saldo Inicial</th>
                    <th className='text-end'>Saldo Final</th>
                  </tr>
                }>
                <tbody>
                  {displayed.map((item, idx) => (
                    <tr key={keyOf(item)} className={selected.find(s => keyOf(s) === keyOf(item)) ? 'table-success' : ''} onClick={() => toggleSelect(item)} style={{ cursor: 'pointer' }}>
                      <td>{setPagination.startIndex + idx + 1}</td>
                      <td>{item.ano}</td>
                      <td>{
                        (programas.find(p => p.id === item.idPrograma)?.descricao) || item.idPrograma
                      }</td>
                      <td>{item.idConta}</td>
                      <td className="text-end">{formatCurrency(item.inicial)}</td>
                      <td className="text-end">{formatCurrency(item.saldoFinal)}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      {/* Modal de cadastro/edição */}
      <SaldoInvestimentoModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} handleChange={handleChange} programas={programas} contas={contas} />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}


